/**
 * DeliverableStudio — one "Generate deliverables" pop-out that replaces the wall
 * of individual export buttons. Tick what you want, Generate, and ready items are
 * highlighted (greyed + ✅) with a re-download. Reused by the assessment run view
 * and the ENTERPRISE360 blueprint view.
 *
 * groups: [{ id, label, tone?: "primary"|"danger", hint?, items: [{ id, label, url, note? }] }]
 */
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  X, Download, CheckCircle2, Loader2, AlertTriangle, Square, CheckSquare, Sparkles, Lock,
} from "lucide-react";

async function fetchDownload(url) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename="?([^";]+)"?/);
  const filename = m ? m[1] : (url.split("?")[0].split("/").pop() || "deliverable");
  const blob = await res.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(u);
}

export default function DeliverableStudio({ open, onClose, title = "Generate deliverables", subtitle, groups = [] }) {
  const [selected, setSelected] = useState({});
  const [status, setStatus] = useState({}); // id -> 'busy' | 'done' | 'error'
  const [running, setRunning] = useState(false);

  if (!open) return null;
  const allItems = groups.flatMap((g) => g.items);
  const selectedItems = allItems.filter((i) => selected[i.id]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleGroup = (g) => {
    const allOn = g.items.every((i) => selected[i.id]);
    setSelected((s) => { const n = { ...s }; g.items.forEach((i) => { n[i.id] = !allOn; }); return n; });
  };

  const runItem = async (item) => {
    setStatus((s) => ({ ...s, [item.id]: "busy" }));
    try { await fetchDownload(item.url); setStatus((s) => ({ ...s, [item.id]: "done" })); return true; }
    catch (e) { setStatus((s) => ({ ...s, [item.id]: "error" })); toast.error(`${item.label}: ${e.message || e}`); return false; }
  };

  const generate = async () => {
    if (!selectedItems.length) { toast.error("Tick at least one deliverable to generate"); return; }
    setRunning(true);
    let ok = 0;
    for (const item of selectedItems) { if (await runItem(item)) ok += 1; }
    setRunning(false);
    if (ok) toast.success(`${ok} deliverable${ok > 1 ? "s" : ""} generated`);
  };

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="deliverable-studio">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={running ? undefined : onClose} />
      <div className="relative bg-white w-full max-w-2xl max-h-[88vh] flex flex-col shadow-2xl border border-zinc-200">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-zinc-200">
          <div>
            <div className="flex items-center gap-2 text-[#7737F6] text-[11px] font-bold uppercase tracking-[0.18em]">
              <Sparkles size={13} /> Deliverable Studio
            </div>
            <h3 className="text-xl font-semibold text-black mt-1">{title}</h3>
            {subtitle && <p className="text-[13px] text-zinc-500 mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} disabled={running} className="text-zinc-400 hover:text-black disabled:opacity-40" data-testid="studio-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-4 space-y-5">
          {groups.map((g) => {
            const allOn = g.items.every((i) => selected[i.id]);
            const danger = g.tone === "danger";
            const primary = g.tone === "primary";
            if (primary) {
              return (
                <div key={g.id} data-testid={`studio-group-${g.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#7737F6]">
                      <Sparkles size={12} />{g.label}
                    </div>
                    <button onClick={() => toggleGroup(g)} className="text-[11px] text-[#7737F6] hover:underline" data-testid={`studio-selectall-${g.id}`}>
                      {allOn ? "Clear" : "Select all"}
                    </button>
                  </div>
                  {g.hint && <p className="text-[11px] text-zinc-400 mb-3">{g.hint}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {g.items.map((item) => {
                      const st = status[item.id];
                      const on = !!selected[item.id];
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => toggle(item.id)}
                          data-testid={`studio-item-${item.id}`}
                          className={`text-left p-4 border flex flex-col gap-2 transition-colors ${
                            st === "done" ? "bg-emerald-50/60 border-emerald-200" :
                            st === "error" ? "bg-rose-50 border-rose-300" :
                            on ? "bg-[#F7F4FF] border-[#7737F6]" : "bg-zinc-50 border-zinc-200 hover:border-[#7737F6] hover:bg-white"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[#7737F6]" data-testid={`studio-check-${item.id}`}>
                              {on ? <CheckSquare size={18} /> : <Square size={18} className="text-zinc-400" />}
                            </span>
                            {st === "busy" && <Loader2 size={15} className="animate-spin text-[#7737F6]" />}
                            {st === "done" && <CheckCircle2 size={15} className="text-emerald-600" />}
                            {st === "error" && <AlertTriangle size={15} className="text-rose-600" />}
                          </div>
                          <div className={`text-[13px] font-medium leading-snug ${st === "done" ? "text-zinc-500" : "text-zinc-900"}`}>{item.label}</div>
                          {st === "done" && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); runItem(item); }} className="text-[11px] text-[#7737F6] hover:underline inline-flex items-center gap-1 self-start" data-testid={`studio-redownload-${item.id}`}>
                              <Download size={11} /> Download again
                            </button>
                          )}
                          {st === "error" && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); runItem(item); }} className="text-[11px] text-rose-600 hover:underline inline-flex items-center gap-1 self-start" data-testid={`studio-retry-${item.id}`}>
                              <AlertTriangle size={11} /> Retry
                            </button>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            }
            return (
              <div key={g.id} data-testid={`studio-group-${g.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] ${danger ? "text-rose-600" : "text-zinc-500"}`}>
                    {danger && <Lock size={12} />}{g.label}
                  </div>
                  <button onClick={() => toggleGroup(g)} className="text-[11px] text-[#7737F6] hover:underline" data-testid={`studio-selectall-${g.id}`}>
                    {allOn ? "Clear" : "Select all"}
                  </button>
                </div>
                {g.hint && <p className="text-[11px] text-zinc-400 mb-2">{g.hint}</p>}
                <div className="space-y-1.5">
                  {g.items.map((item) => {
                    const st = status[item.id];
                    const on = !!selected[item.id];
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 px-3 py-2.5 border transition-colors ${
                          st === "done" ? "bg-emerald-50/60 border-emerald-200" :
                          st === "error" ? "bg-rose-50 border-rose-300" :
                          on ? (danger ? "bg-rose-50 border-rose-300" : "bg-[#F7F4FF] border-[#7737F6]") :
                          "bg-white border-zinc-200 hover:border-zinc-300"
                        }`}
                        data-testid={`studio-item-${item.id}`}
                      >
                        <button onClick={() => toggle(item.id)} className="shrink-0 text-[#7737F6]" data-testid={`studio-check-${item.id}`}>
                          {on ? <CheckSquare size={18} /> : <Square size={18} className="text-zinc-400" />}
                        </button>
                        <div className={`flex-1 min-w-0 ${st === "done" ? "text-zinc-500" : "text-zinc-800"}`}>
                          <div className="text-[13px] font-medium truncate">{item.label}</div>
                          {item.note && <div className="text-[11px] text-zinc-400 truncate">{item.note}</div>}
                        </div>
                        <div className="shrink-0 w-24 flex items-center justify-end gap-1.5">
                          {st === "busy" && <Loader2 size={15} className="animate-spin text-[#7737F6]" />}
                          {st === "done" && (
                            <>
                              <CheckCircle2 size={15} className="text-emerald-600" />
                              <button onClick={() => runItem(item)} className="text-[11px] text-[#7737F6] hover:underline inline-flex items-center gap-1" data-testid={`studio-redownload-${item.id}`}>
                                <Download size={11} /> Again
                              </button>
                            </>
                          )}
                          {st === "error" && (
                            <button onClick={() => runItem(item)} className="text-[11px] text-rose-600 hover:underline inline-flex items-center gap-1" data-testid={`studio-retry-${item.id}`}>
                              <AlertTriangle size={11} /> Retry
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-200 bg-zinc-50">
          <div className="text-[12px] text-zinc-500" data-testid="studio-selected-count">
            {selectedItems.length} selected
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} disabled={running} className="text-[13px] text-zinc-600 hover:text-black px-3 py-2 disabled:opacity-40">Close</button>
            <button
              onClick={generate}
              disabled={running || !selectedItems.length}
              className="inline-flex items-center gap-2 bg-[#7737F6] text-white text-[13px] font-medium px-5 py-2.5 hover:bg-[#5f29c9] disabled:opacity-50 transition-colors"
              data-testid="studio-generate"
            >
              {running ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              {running ? "Generating…" : `Generate selected${selectedItems.length ? ` (${selectedItems.length})` : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
